import React, { useState, useEffect } from 'react';
import { formatDate } from '../../utils/dateFormat';
import { Lead } from '../../types/lead';
import { supabase, lookupCompanyInCVR } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';

interface LeadDetailsProps {
  lead: Lead;
  onClose: () => void;
  onUpdate?: () => void;
}

interface LeadPost {
  id: string;
  created_at: string;
  job_title: string;
  job_description: string;
  job_type: string;
  location: string;
  salary: string;
  application_deadline: string;
  jobnet_id: string;
  jobnet_url: string;
  posted_at: string;
  company_name: string;
  company_address: string;
}

const INTERACTION_TYPES = ['Call', 'Email', 'Meeting', 'LinkedIn', 'Other'] as const;
type InteractionType = typeof INTERACTION_TYPES[number];

interface NewInteraction {
  type: InteractionType;
  summary: string;
  duration?: string;
  date: string;
}

const LeadDetails: React.FC<LeadDetailsProps> = ({ lead, onClose, onUpdate }) => {
  const { user } = useAuth();
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingCVR, setCheckingCVR] = useState(false);
  const [cvrMessage, setCvrMessage] = useState<string | null>(null);
  const [leadPosts, setLeadPosts] = useState<LeadPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [companyData, setCompanyData] = useState(lead);
  const [newInteraction, setNewInteraction] = useState<NewInteraction>({
    type: INTERACTION_TYPES[0],
    summary: '',
    duration: '',
    date: new Date().toISOString()
  });

  useEffect(() => {
    fetchLeadPosts();
  }, [lead.id]);

  const fetchLeadPosts = async () => {
    setLoadingPosts(true);
    try {
      const { data, error } = await supabase
        .from('leads_posts')
        .select('*')
        .eq('lead_id', lead.id)
        .order('posted_at', { ascending: false });

      if (error) {
        console.error('Error fetching lead posts:', error);
        return;
      }

      setLeadPosts(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoadingPosts(false);
    }
  };

  const handleAddInteraction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newInteraction.summary.trim()) return;

    setLoading(true);
    try {
      const updatedInteractions = [...(lead.interactions || []), {
        ...newInteraction,
        by: user?.email || 'Unknown',
        date: new Date().toISOString()
      }];

      const { error } = await supabase
        .from('leads')
        .update({ 
          interactions: updatedInteractions,
          last_contacted_at: new Date().toISOString()
        })
        .eq('id', lead.id);

      if (error) throw error;

      setNewInteraction({
        type: INTERACTION_TYPES[0],
        summary: '',
        duration: '',
        date: new Date().toISOString()
      });
      onUpdate?.();
    } catch (error) {
      console.error('Error adding interaction:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    setLoading(true);
    try {
      const newNoteObj = {
        date: new Date().toISOString(),
        content: newNote.trim(),
        by: user?.email || 'Unknown'
      };

      const updatedNotes = [...(lead.notes || []), newNoteObj];

      const { error } = await supabase
        .from('leads')
        .update({ notes: updatedNotes })
        .eq('id', lead.id);

      if (error) throw error;

      setNewNote('');
      onUpdate?.();
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckCVR = async () => {
    setCheckingCVR(true);
    setCvrMessage(null);
    try {
      const response = await lookupCompanyInCVR(lead.id);
      setCvrMessage(response.message);
      if (response.data) {
        setCompanyData({
          ...lead,
          cvr: response.data.cvr,
          company_size: response.data.company_size,
          industry_code: response.data.industry_code,
          company_activities: response.data.company_activities,
          hiring_activity: response.data.hiring_activity,
          advertising_protection: response.data.advertising_protection
        });
      }
      onUpdate?.();
    } catch (error) {
      console.error('Error checking CVR:', error);
      setCvrMessage('Failed to check CVR data. Please try again.');
    } finally {
      setCheckingCVR(false);
      setTimeout(() => setCvrMessage(null), 5000);
    }
  };

  useEffect(() => {
    setCompanyData(lead);
  }, [lead]);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-bold">{lead.company}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* CVR Message */}
        {cvrMessage && (
          <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded-md whitespace-pre-line">
            {cvrMessage}
          </div>
        )}

        {/* Lead Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Contact Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Contact Information</h3>
            <div className="space-y-2">
              <p><span className="font-medium">Name:</span> {lead.contact_name || 'N/A'}</p>
              <p><span className="font-medium">Email:</span> {lead.contact_email || 'N/A'}</p>
              <p><span className="font-medium">Phone:</span> {lead.contact_phone || 'N/A'}</p>
              <p><span className="font-medium">Status:</span> {lead.status}</p>
              <p><span className="font-medium">Assigned To:</span> {lead.assigned_to || 'Unassigned'}</p>
            </div>
          </div>

          {/* Company Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-semibold">Company Information</h3>
              <Button
                variant="contained"
                color={
                  companyData.cvr_status === 'checked_with_data' ? "success" :
                  companyData.cvr_status === 'checked_no_data' ? "error" :
                  "primary"
                }
                size="small"
                onClick={handleCheckCVR}
                disabled={checkingCVR || companyData.cvr_status?.includes('checked')}
                startIcon={<BusinessIcon />}
              >
                {checkingCVR ? 'Checking...' : 
                 companyData.cvr_status === 'checked_with_data' ? 'CVR Found' :
                 companyData.cvr_status === 'checked_no_data' ? 'No CVR Data' :
                 'Check CVR'}
              </Button>
            </div>
            <div className="space-y-2">
              <p><span className="font-medium">Company:</span> {companyData.company}</p>
              <p><span className="font-medium">CVR:</span> {companyData.cvr || 'N/A'}</p>
              <p><span className="font-medium">Size:</span> {companyData.company_size || 'N/A'}</p>
              <p><span className="font-medium">Industry:</span> {companyData.industry_code ? `${companyData.industry_code} - ${companyData.company_activities}` : 'N/A'}</p>
              <p><span className="font-medium">Hiring Activity:</span> <span className="capitalize">{companyData.hiring_activity || 'N/A'}</span></p>
              <p><span className="font-medium">Reklamebeskyttelse:</span> {companyData.advertising_protection && String(companyData.advertising_protection).includes('protect') ? 'Ja' : 'Nej'}</p>
            </div>
          </div>

          {/* Source Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Source Information</h3>
            <div className="space-y-2">
              <p><span className="font-medium">Source:</span> {lead.referrer || 'N/A'}</p>
              <p><span className="font-medium">UTM Source:</span> {lead.utm_source || 'N/A'}</p>
              <p><span className="font-medium">UTM Medium:</span> {lead.utm_medium || 'N/A'}</p>
              <p><span className="font-medium">Details:</span> {lead.source_details || 'N/A'}</p>
            </div>
          </div>

          {/* Activity Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Activity Information</h3>
            <div className="space-y-2">
              <p><span className="font-medium">Created:</span> {formatDate(lead.created_at)}</p>
              <p><span className="font-medium">Last Contact:</span> {lead.last_contacted_at ? formatDate(lead.last_contacted_at) : 'Never'}</p>
              <p><span className="font-medium">Status:</span> {lead.is_active ? 'Active' : 'Inactive'}</p>
              <p><span className="font-medium">Interactions:</span> {lead.interactions?.length || 0}</p>
            </div>
          </div>
        </div>

        {/* Interactions Section */}
        <div className="mb-6">
          <h3 className="font-semibold mb-2">Interactions</h3>
          <div className="space-y-4">
            <form onSubmit={handleAddInteraction} className="space-y-2">
              <div className="flex gap-2">
                <select
                  value={newInteraction.type}
                  onChange={(e) => setNewInteraction({...newInteraction, type: e.target.value as InteractionType})}
                  className="rounded border p-2"
                >
                  {INTERACTION_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Duration (optional)"
                  value={newInteraction.duration}
                  onChange={(e) => setNewInteraction({...newInteraction, duration: e.target.value})}
                  className="rounded border p-2 w-32"
                />
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Interaction summary"
                  value={newInteraction.summary}
                  onChange={(e) => setNewInteraction({...newInteraction, summary: e.target.value})}
                  className="rounded border p-2 flex-1"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            </form>
            
            <div className="space-y-2">
              {lead.interactions?.map((interaction, index) => (
                <div key={index} className="bg-gray-50 p-3 rounded">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{interaction.type}</span>
                    <span className="text-gray-500">{formatDate(interaction.date)}</span>
                  </div>
                  <p className="text-sm mt-1">{interaction.summary}</p>
                  {interaction.duration && (
                    <p className="text-sm text-gray-500 mt-1">Duration: {interaction.duration}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">By: {interaction.by}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Notes Section */}
        <div className="mb-6">
          <h3 className="font-semibold mb-2">Notes</h3>
          <form onSubmit={handleAddNote} className="space-y-2 mb-4">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note..."
              className="w-full rounded border p-2"
              rows={3}
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
            >
              Add Note
            </button>
          </form>
          
          <div className="space-y-2">
            {lead.notes?.map((note, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{note.by}</span>
                  <span className="text-gray-500">{formatDate(note.date)}</span>
                </div>
                <p className="text-sm mt-1">{note.content}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Job Posts Section */}
        <div className="mb-6">
          <h3 className="font-semibold mb-2">Job Posts</h3>
          {loadingPosts ? (
            <div className="text-center py-4">Loading job posts...</div>
          ) : leadPosts.length === 0 ? (
            <div className="text-gray-500 italic">No job posts found</div>
          ) : (
            <div className="space-y-4">
              {leadPosts.map((post) => (
                <div key={post.id} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-lg">{post.job_title}</h4>
                    <span className="text-sm text-gray-500">
                      Posted: {formatDate(post.posted_at)}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm"><span className="font-medium">Company:</span> {post.company_name}</p>
                      <p className="text-sm"><span className="font-medium">Location:</span> {post.location}</p>
                      <p className="text-sm"><span className="font-medium">Type:</span> {post.job_type}</p>
                      {post.salary && (
                        <p className="text-sm"><span className="font-medium">Salary:</span> {post.salary}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm"><span className="font-medium">Deadline:</span> {formatDate(post.application_deadline)}</p>
                      <p className="text-sm"><span className="font-medium">Address:</span> {post.company_address}</p>
                      {post.jobnet_url && (
                        <a 
                          href={post.jobnet_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-600 text-sm inline-block mt-2"
                        >
                          View on Jobnet →
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 line-clamp-3">{post.job_description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadDetails; 